import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Wind, MapPin, Building2, Info, Search } from 'lucide-react';
import { departmentService, roomService } from '../../services/firebaseService';
import EnvironmentalAssessment from './EnvironmentalAssessment';
import './EnvironmentalAssessmentManager.css';

/**
 * Environmental Assessment Manager
 * Standalone page for assessing environmental conditions
 * Accessible from main navigation
 */
const EnvironmentalAssessmentManager = () => {
  const { t } = useTranslation('patientRisk');
  const [viewType, setViewType] = useState('departments'); // 'departments' or 'rooms'
  const [departments, setDepartments] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Load data
  React.useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [depts, rms] = await Promise.all([
          departmentService.getAll(),
          roomService.getAll()
        ]);
        setDepartments(depts);
        setRooms(rms);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);
  
  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
  };
  
  const handleAssessmentUpdate = async (location, conditions) => {
    try {
      const updates = {
        environmentalConditions: conditions
      };
      
      if (viewType === 'departments') {
        await departmentService.update(location.id, updates);
        // Update local state
        setDepartments(prev => prev.map(d => 
          d.id === location.id ? { ...d, ...updates } : d
        ));
      } else {
        await roomService.update(location.id, updates);
        // Update local state
        setRooms(prev => prev.map(r => 
          r.id === location.id ? { ...r, ...updates } : r
        ));
      }
      
      console.log('✅ Environmental assessment saved for:', location.name || location.number);
    } catch (error) {
      console.error('Error saving environmental assessment:', error);
      alert(t('failedToSaveAssessment'));
    }
  };
  
  const getEnvironmentalStatus = (location) => {
    const factorCount = location.environmentalConditions?.activeFactorIds?.length || 0;
    const multiplier = location.environmentalConditions?.multiplier || 1.0;
    
    if (factorCount === 0) {
      return { status: t('notAssessed'), color: '#999', icon: '○' };
    }
    
    if (multiplier >= 1.7) {
      return { status: t('highRiskStatus'), color: '#f44336', icon: '🔴' };
    }
    if (multiplier >= 1.5) {
      return { status: t('mediumRiskStatus'), color: '#ff9800', icon: '🟠' };
    }
    if (multiplier >= 1.3) {
      return { status: t('lowRiskStatus'), color: '#ffc107', icon: '🟡' };
    }
    return { status: t('minimalRisk'), color: '#4caf50', icon: '🟢' };
  };
  
  const getFilteredLocations = () => {
    const locations = viewType === 'departments' ? departments : rooms;
    
    if (!searchQuery) return locations;
    
    const query = searchQuery.toLowerCase();
    return locations.filter(loc => 
      (loc.name && loc.name.toLowerCase().includes(query)) ||
      (loc.number && loc.number.toLowerCase().includes(query))
    );
  };
  
  const filteredLocations = getFilteredLocations();
  
  return (
    <div className="environmental-assessment-manager">
      <div className="manager-header">
        <div className="header-title">
          <h1>
            <Wind size={28} />
            {t('environmentalAssessmentManager')}
          </h1>
          <p>{t('assessEnvRiskConditions')}</p>
        </div>
      </div>
      
      {/* View Type Toggle */}
      <div className="view-type-selector">
        <button
          className={`view-type-btn ${viewType === 'departments' ? 'active' : ''}`}
          onClick={() => {
            setViewType('departments');
            setSelectedLocation(null);
          }}
        >
          <Building2 size={20} />
          {t('departments')} ({departments.length})
        </button>
        <button
          className={`view-type-btn ${viewType === 'rooms' ? 'active' : ''}`}
          onClick={() => {
            setViewType('rooms');
            setSelectedLocation(null);
          }}
        >
          <MapPin size={20} />
          {t('rooms')} ({rooms.length})
        </button>
      </div>
      
      <div className="assessment-layout">
        {/* Location List */}
        <div className="location-list-panel">
          <div className="panel-header">
            <h3>{t('selectDepartmentOrRoomToAssess', { viewType: viewType === 'departments' ? t('department') : t('room') })}</h3>
            <div className="search-box">
              <Search size={16} />
              <input
                type="text"
                placeholder={t('searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          
          {loading ? (
            <div className="loading-state">{t('loading')}</div>
          ) : filteredLocations.length === 0 ? (
            <div className="empty-state">
              <Info size={32} color="#ccc" />
              <p>{t('noViewTypeFound', { viewType: viewType === 'departments' ? t('department') : t('room') })}</p>
            </div>
          ) : (
            <div className="location-cards">
              {filteredLocations.map(location => {
                const status = getEnvironmentalStatus(location);
                const isSelected = selectedLocation?.id === location.id;
                const factorCount = location.environmentalConditions?.activeFactorIds?.length || 0;
                
                return (
                  <div
                    key={location.id}
                    className={`location-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleLocationSelect(location)}
                  >
                    <div className="location-card-header">
                      <h4>{location.name || location.number}</h4>
                      <span 
                        className="status-indicator"
                        style={{ color: status.color }}
                      >
                        {status.icon}
                      </span>
                    </div>
                    <div className="location-card-info">
                      <span className="location-type">
                        {viewType === 'departments' ? t('department') : t('room')}
                      </span>
                      {factorCount > 0 && (
                        <span className="factor-count">
                          {t('factorsCount', { count: factorCount })}
                        </span>
                      )}
                    </div>
                    <div className="location-card-footer">
                      <span className="status-label">{status.status}</span>
                      {location.environmentalConditions?.multiplier && (
                        <span className="multiplier-display">
                          ×{location.environmentalConditions.multiplier.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Assessment Panel */}
        <div className="assessment-panel">
          {selectedLocation ? (
            <>
              <div className="panel-header">
                <h3>
                  {t('environmentalAssessmentTitle', { name: selectedLocation.name || selectedLocation.number })}
                </h3>
                <span className="location-type-badge">
                  {viewType === 'departments' ? `🏬 ${t('departmentBadge')}` : `🚪 ${t('roomBadge')}`}
                </span>
              </div>
              
              <EnvironmentalAssessment
                location={selectedLocation}
                departmentId={viewType === 'departments' ? selectedLocation.id : selectedLocation.departmentId}
                embedded={false}
                showMultiplierPreview={true}
                onUpdate={(conditions) => handleAssessmentUpdate(selectedLocation, conditions)}
                initialFactorIds={selectedLocation.environmentalConditions?.activeFactorIds || []}
              />
            </>
          ) : (
            <div className="no-selection">
              <Wind size={64} color="#ccc" />
              <h3>{t('noLocationSelected')}</h3>
              <p>{t('selectLocationFromList', { viewType: viewType === 'departments' ? t('department') : t('room') })}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalAssessmentManager;


